import type {
  EquipmentCategory,
  EquipmentInstance,
  EquipmentObjectType,
  EquipmentPoint,
  EquipmentPort,
  EquipmentProcessType,
  EquipmentStyle,
} from '../features/equipment/equipmentTypes';
import type { ElementType, LayoutElement } from '../types';
import { ELEMENT_COLORS } from '../constants';
import { DEFAULT_LAYER_FOR_TYPE } from '../features/layers/layersSlice';
import {
  DEFAULT_STOCKER_PROCESS_WAIT_SEC,
  normalizeEquipmentProcessType,
} from '../features/equipment/equipmentConstants';
import {
  primaryProcessType,
  readStockerProcessTypes,
} from '../features/equipment/stockerProcessTypes';
import { polygonBounds, resolvePorts, resolveStyle } from './equipmentGeometry';
import { defaultElementDepth } from './layout3d';

/** Embedded on layout elements so backend layout save/load can restore overlays. */
export const EQUIPMENT_OVERLAY_PROP = 'equipmentOverlay';

export type EquipmentOverlaySnapshot = {
  version: 1;
  id: string;
  templateId: string | null;
  name: string;
  objectType: EquipmentObjectType;
  category: EquipmentCategory;
  equipmentType: EquipmentProcessType;
  processTypes?: EquipmentProcessType[] | null;
  description: string;
  tags: string[];
  notes: string;
  polygon: EquipmentPoint[];
  rotation: number;
  ports: EquipmentPort[];
  style: EquipmentStyle;
  properties?: Record<string, unknown>;
  foupCapacity?: number;
  outsideStocker?: boolean;
  processWaitSec?: number;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
};

export type StockerExportPoint = { x: number; y: number };

export type StockerExportBounds = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  width: number;
  height: number;
};

/**
 * One DXF INSERT stocker in raw drawing coordinates.
 *
 * Geometry strategy (all three included on purpose):
 * - `position` — bbox center (best single placement / routing point)
 * - `bounds` — axis-aligned footprint (best for clearance / collision)
 * - `polygon` — bbox corners (best for drawing overlays)
 * - `insert` — CAD INSERT origin (may differ from geometric center)
 */
export type StockerExportRecord = {
  id: string;
  name: string;
  source: 'dxf-insert';
  blockName: string | null;
  layer: string | null;
  process: string | null;
  workCell: string | null;
  /** Geometric center of the raw footprint bbox. */
  position: StockerExportPoint;
  /** Axis-aligned raw footprint. */
  bounds: StockerExportBounds;
  /** Raw footprint polygon (bbox corners, CCW). */
  polygon: StockerExportPoint[];
  /** CAD INSERT insertion point + rotation when available. */
  insert: {
    x: number | null;
    y: number | null;
    rotation: number;
  };
};

export type StockerExportDoc = {
  kind: 'dxf-stocker-export';
  version: '1.1.0';
  exportedAt: string;
  floorPlanId: string | null;
  floorPlanName: string | null;
  /** All geometry fields use DXF drawing / raw space (not canvas). */
  coordinateSpace: 'raw-dxf';
  geometry: {
    position: 'bbox-center';
    bounds: 'axis-aligned-bbox';
    polygon: 'bbox-corners';
  };
  count: number;
  stockers: StockerExportRecord[];
};

type RawBounds = { minX: number; minY: number; maxX: number; maxY: number };

const stringMeta = (metadata: Record<string, unknown>, key: string): string | null => {
  const value = metadata[key];
  return typeof value === 'string' && value.trim() ? value : null;
};

const numOrNull = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const isRawBounds = (value: unknown): value is RawBounds => {
  if (!value || typeof value !== 'object') return false;
  const b = value as Record<string, unknown>;
  return (
    Number.isFinite(b.minX) &&
    Number.isFinite(b.minY) &&
    Number.isFinite(b.maxX) &&
    Number.isFinite(b.maxY)
  );
};

/** True when this instance came from a DXF INSERT stocker extraction. */
export function isDxfInsertStocker(instance: EquipmentInstance): boolean {
  if (instance.objectType !== 'stocker') return false;
  const metadata = instance.metadata ?? {};
  const source = stringMeta(metadata, 'source');
  if (source === 'backend-json-insert') return true;
  if (instance.tags?.includes('insert-extraction')) return true;
  if (isRawBounds(metadata.rawBounds)) return true;
  const insert = metadata.insert;
  if (insert && typeof insert === 'object') {
    const rec = insert as Record<string, unknown>;
    if (numOrNull(rec.x) != null || numOrNull(rec.y) != null) return true;
  }
  return false;
}

function boundsFromRaw(raw: RawBounds): StockerExportBounds {
  return {
    minX: raw.minX,
    minY: raw.minY,
    maxX: raw.maxX,
    maxY: raw.maxY,
    width: raw.maxX - raw.minX,
    height: raw.maxY - raw.minY,
  };
}

function centerFromBounds(bounds: StockerExportBounds): StockerExportPoint {
  return {
    x: (bounds.minX + bounds.maxX) / 2,
    y: (bounds.minY + bounds.maxY) / 2,
  };
}

function polygonFromBounds(bounds: StockerExportBounds): StockerExportPoint[] {
  return [
    { x: bounds.minX, y: bounds.minY },
    { x: bounds.maxX, y: bounds.minY },
    { x: bounds.maxX, y: bounds.maxY },
    { x: bounds.minX, y: bounds.maxY },
  ];
}

function recordFromDxfInsertStocker(instance: EquipmentInstance): StockerExportRecord | null {
  if (!isDxfInsertStocker(instance)) return null;

  const metadata = instance.metadata ?? {};
  const props = instance.overrides?.properties ?? {};
  const rawBounds = isRawBounds(metadata.rawBounds) ? metadata.rawBounds : null;
  if (!rawBounds) return null;

  const bounds = boundsFromRaw(rawBounds);
  const position = centerFromBounds(bounds);
  const polygon = polygonFromBounds(bounds);
  const insertMeta =
    metadata.insert && typeof metadata.insert === 'object'
      ? (metadata.insert as Record<string, unknown>)
      : null;

  return {
    id: instance.id,
    name: instance.name,
    source: 'dxf-insert',
    blockName:
      stringMeta(metadata, 'block') ??
      (typeof props.blockName === 'string' ? props.blockName : null),
    layer: stringMeta(metadata, 'layer') ?? (typeof props.layer === 'string' ? props.layer : null),
    process:
      stringMeta(metadata, 'process') ?? (typeof props.process === 'string' ? props.process : null),
    workCell: stringMeta(metadata, 'workCell'),
    position,
    bounds,
    polygon,
    insert: {
      x: insertMeta ? numOrNull(insertMeta.x) : null,
      y: insertMeta ? numOrNull(insertMeta.y) : null,
      rotation: insertMeta ? (numOrNull(insertMeta.rotation) ?? 0) : 0,
    },
  };
}

/** Build a downloadable DXF INSERT stocker export in raw drawing coordinates. */
export function buildStockerExport(params: {
  equipmentInstances: EquipmentInstance[];
  /** When set, only these instance IDs are included (still must be DXF INSERT stockers). */
  instanceIds?: string[] | null;
  floorPlanId?: string | null;
  floorPlanName?: string | null;
}): StockerExportDoc {
  const stockers: StockerExportRecord[] = [];
  const idFilter =
    params.instanceIds && params.instanceIds.length > 0 ? new Set(params.instanceIds) : null;

  for (const instance of params.equipmentInstances) {
    if (idFilter && !idFilter.has(instance.id)) continue;
    const record = recordFromDxfInsertStocker(instance);
    if (record) stockers.push(record);
  }

  return {
    kind: 'dxf-stocker-export',
    version: '1.1.0',
    exportedAt: new Date().toISOString(),
    floorPlanId: params.floorPlanId ?? null,
    floorPlanName: params.floorPlanName ?? null,
    coordinateSpace: 'raw-dxf',
    geometry: {
      position: 'bbox-center',
      bounds: 'axis-aligned-bbox',
      polygon: 'bbox-corners',
    },
    count: stockers.length,
    stockers,
  };
}

export function stockerExportFilename(floorPlanName?: string | null): string {
  if (floorPlanName) {
    const base = floorPlanName.replace(/\.[^.]+$/, '');
    return `${base}-dxf-stockers`;
  }
  return 'dxf-stockers';
}

/** Map overlay object type → layout element type for save-into-elements. */
function layoutTypeForInstance(instance: EquipmentInstance): ElementType {
  if (isDxfInsertStocker(instance) || instance.objectType === 'stocker') return 'stocker';
  if (instance.objectType === 'buffer') return 'stb';
  if (instance.objectType === 'lift') return 'lifter';
  return 'rectangle';
}

/** Snapshot rich enough to rebuild an overlay after backend layout round-trip. */
export function equipmentOverlaySnapshotFromInstance(
  instance: EquipmentInstance
): EquipmentOverlaySnapshot {
  const style = resolveStyle(instance, null);
  const ports = resolvePorts(instance, null).map((p) => ({
    ...p,
    parentId: instance.id,
    parentType: instance.objectType === 'stocker' ? ('stocker' as const) : ('equipment' as const),
  }));
  return {
    version: 1,
    id: instance.id,
    templateId: instance.templateId,
    name: instance.name,
    objectType: instance.objectType,
    category: instance.category,
    equipmentType: instance.equipmentType,
    processTypes: instance.processTypes,
    description: instance.description ?? '',
    tags: instance.tags ?? [],
    notes: instance.notes ?? '',
    polygon: instance.polygon.map((p) => ({ x: p.x, y: p.y })),
    rotation: instance.rotation ?? 0,
    ports,
    style,
    properties: instance.overrides?.properties ? { ...instance.overrides.properties } : undefined,
    foupCapacity: instance.foupCapacity,
    outsideStocker: instance.outsideStocker,
    processWaitSec: instance.processWaitSec,
    metadata: { ...(instance.metadata ?? {}) },
    createdAt: instance.createdAt,
    updatedAt: instance.updatedAt,
  };
}

function isEquipmentPoint(value: unknown): value is EquipmentPoint {
  if (!value || typeof value !== 'object') return false;
  const p = value as Record<string, unknown>;
  return Number.isFinite(p.x) && Number.isFinite(p.y);
}

function asEquipmentOverlaySnapshot(value: unknown): EquipmentOverlaySnapshot | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  if (raw.version !== 1) return null;
  const id = typeof raw.id === 'string' ? raw.id.trim() : '';
  const name = typeof raw.name === 'string' ? raw.name : '';
  const objectType = typeof raw.objectType === 'string' ? raw.objectType : '';
  const category = typeof raw.category === 'string' ? raw.category : 'custom';
  const equipmentType = normalizeEquipmentProcessType(raw.equipmentType);
  if (!id || !objectType) return null;
  if (!Array.isArray(raw.polygon) || raw.polygon.length < 2) return null;
  const polygon = raw.polygon.filter(isEquipmentPoint);
  if (polygon.length < 2) return null;
  const ports = Array.isArray(raw.ports) ? (raw.ports as EquipmentPort[]) : [];
  const styleRaw =
    raw.style && typeof raw.style === 'object' ? (raw.style as EquipmentStyle) : null;
  const style: EquipmentStyle = styleRaw ?? {
    color: '#2563eb',
    fillOpacity: 0.14,
    icon: '',
  };

  return {
    version: 1,
    id,
    templateId: typeof raw.templateId === 'string' ? raw.templateId : null,
    name: name || id,
    objectType: objectType as EquipmentObjectType,
    category: category as EquipmentCategory,
    equipmentType,
    processTypes: Array.isArray(raw.processTypes)
      ? raw.processTypes.map(normalizeEquipmentProcessType)
      : raw.processTypes === null
        ? null
        : undefined,
    description: typeof raw.description === 'string' ? raw.description : '',
    tags: Array.isArray(raw.tags) ? raw.tags.filter((t): t is string => typeof t === 'string') : [],
    notes: typeof raw.notes === 'string' ? raw.notes : '',
    polygon,
    rotation: typeof raw.rotation === 'number' && Number.isFinite(raw.rotation) ? raw.rotation : 0,
    ports,
    style,
    properties:
      raw.properties && typeof raw.properties === 'object'
        ? (raw.properties as Record<string, unknown>)
        : undefined,
    foupCapacity: typeof raw.foupCapacity === 'number' ? raw.foupCapacity : undefined,
    outsideStocker: typeof raw.outsideStocker === 'boolean' ? raw.outsideStocker : undefined,
    processWaitSec: typeof raw.processWaitSec === 'number' ? raw.processWaitSec : undefined,
    metadata:
      raw.metadata && typeof raw.metadata === 'object'
        ? (raw.metadata as Record<string, unknown>)
        : {},
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : new Date().toISOString(),
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : new Date().toISOString(),
  };
}

/**
 * Rebuild an equipment overlay instance from a layout element that was saved
 * from the marked equipment list (or from a legacy marked stocker element).
 */
export function equipmentInstanceFromLayoutElement(
  element: LayoutElement
): EquipmentInstance | null {
  const props = element.customProperties ?? {};
  const source = props.source;
  if (source !== 'dxf-insert' && source !== 'dxf-equipment') return null;

  const snapshot = asEquipmentOverlaySnapshot(props[EQUIPMENT_OVERLAY_PROP]);
  if (snapshot) {
    return {
      id: snapshot.id,
      templateId: snapshot.templateId,
      name: snapshot.name,
      objectType: snapshot.objectType,
      category: snapshot.category,
      equipmentType: snapshot.equipmentType,
      processTypes: snapshot.processTypes,
      description: snapshot.description,
      tags: snapshot.tags,
      notes: snapshot.notes,
      polygon: snapshot.polygon,
      rotation: snapshot.rotation,
      overrides: {
        style: snapshot.style,
        ports: snapshot.ports,
        ...(snapshot.properties ? { properties: snapshot.properties } : {}),
      },
      foupCapacity: snapshot.foupCapacity,
      outsideStocker: snapshot.outsideStocker,
      processWaitSec: snapshot.processWaitSec,
      metadata: {
        ...snapshot.metadata,
        restoredFromLayoutElement: true,
      },
      createdAt: snapshot.createdAt,
      updatedAt: snapshot.updatedAt,
    };
  }

  // Legacy fallback (older saves without equipmentOverlay snapshot).
  const dxfId =
    typeof props.dxfEquipmentId === 'string' && props.dxfEquipmentId.trim()
      ? props.dxfEquipmentId.trim()
      : element.id;
  const isStocker = source === 'dxf-insert' || element.type === 'stocker';
  const objectType: EquipmentObjectType =
    typeof props.objectType === 'string'
      ? (props.objectType as EquipmentObjectType)
      : isStocker
        ? 'stocker'
        : element.type === 'stb'
          ? 'buffer'
          : element.type === 'lifter'
            ? 'lift'
            : 'equipment';
  const width = Math.max(0.001, element.dimensions?.width ?? 1);
  const height = Math.max(0.001, element.dimensions?.height ?? 1);
  const x = element.position.x;
  const y = element.position.y;
  const polygon: EquipmentPoint[] = [
    { x, y },
    { x: x + width, y },
    { x: x + width, y: y + height },
    { x, y: y + height },
  ];
  const ports = Array.isArray(props.ports) ? (props.ports as EquipmentPort[]) : [];
  const color = typeof element.metadata?.color === 'string' ? element.metadata.color : '#2563eb';
  const processTypes = readStockerProcessTypes({
    processTypes: props.processTypes as EquipmentProcessType[] | null | undefined,
    legacyProcessType: props.processType as EquipmentProcessType | undefined,
  });

  return {
    id: dxfId,
    templateId: null,
    name: element.metadata?.name || (isStocker ? 'Stocker' : 'Equipment'),
    objectType,
    category: typeof props.category === 'string' ? (props.category as EquipmentCategory) : 'custom',
    equipmentType: normalizeEquipmentProcessType(
      props.equipmentType ?? primaryProcessType(processTypes)
    ),
    processTypes: isStocker ? processTypes : undefined,
    description: typeof props.description === 'string' ? props.description : '',
    tags: [],
    notes: '',
    polygon,
    rotation: element.rotation ?? 0,
    overrides: {
      style: { color, fillOpacity: 0.14, icon: '' },
      ports,
      properties: {
        ...(typeof props.process === 'string' ? { process: props.process } : {}),
        ...(typeof props.workCell === 'string' ? { workCell: props.workCell } : {}),
      },
    },
    foupCapacity: typeof props.foupCapacity === 'number' ? props.foupCapacity : undefined,
    outsideStocker: typeof props.outsideStocker === 'boolean' ? props.outsideStocker : undefined,
    processWaitSec: typeof props.processWaitSec === 'number' ? props.processWaitSec : undefined,
    metadata: {
      source,
      restoredFromLayoutElement: true,
      legacyWithoutOverlaySnapshot: true,
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Convert any equipment overlay instance into a layout element
 * (same shape as toolbox-placed elements). Uses canvas/world footprint.
 */
export function layoutElementFromEquipmentInstance(
  instance: EquipmentInstance
): LayoutElement | null {
  if (!instance.polygon || instance.polygon.length < 2) return null;

  const type = layoutTypeForInstance(instance);
  const bounds = polygonBounds(instance.polygon);
  const width = Math.max(0.001, bounds.maxX - bounds.minX);
  const height = Math.max(0.001, bounds.maxY - bounds.minY);
  const layerId = DEFAULT_LAYER_FOR_TYPE[type] ?? 'layer-1';
  const metadata = instance.metadata ?? {};
  const props = instance.overrides?.properties ?? {};
  const style = resolveStyle(instance, null);
  const overlaySnapshot = equipmentOverlaySnapshotFromInstance(instance);

  const blockName =
    stringMeta(metadata, 'block') ?? (typeof props.blockName === 'string' ? props.blockName : null);
  const layer =
    stringMeta(metadata, 'layer') ?? (typeof props.layer === 'string' ? props.layer : null);
  const process =
    stringMeta(metadata, 'process') ?? (typeof props.process === 'string' ? props.process : null);
  const workCell = stringMeta(metadata, 'workCell');
  const isStocker = type === 'stocker';
  // Carry ports on the element for layout-port UI, and always embed a full
  // overlay snapshot so non-stocker equipment can be restored after API round-trip.
  const ports = overlaySnapshot.ports.map((p) => ({
    ...p,
    parentId: instance.id,
    parentType: isStocker ? ('stocker' as const) : ('equipment' as const),
  }));
  const stockerProcessTypes = readStockerProcessTypes({
    processTypes: instance.processTypes,
    legacyProcessType: instance.equipmentType,
  });

  return {
    id: instance.id,
    type,
    layerId,
    position: {
      x: bounds.minX,
      y: bounds.minY,
      z: 0,
    },
    rotation: instance.rotation ?? 0,
    dimensions: {
      width,
      height,
      depth: defaultElementDepth(type),
    },
    metadata: {
      name: instance.name || (isStocker ? 'Stocker' : 'Equipment'),
      locked: false,
      visible: true,
      color: style.color || ELEMENT_COLORS[type],
      layerId,
    },
    customProperties: {
      ...(isStocker
        ? {
            sku: 'Stocker',
            source: 'dxf-insert',
            ports,
            description: instance.description ?? '',
            processTypes: stockerProcessTypes,
            processType: primaryProcessType(stockerProcessTypes),
            foupCapacity: instance.foupCapacity ?? 100,
            outsideStocker: Boolean(instance.outsideStocker),
            processWaitSec: instance.processWaitSec ?? DEFAULT_STOCKER_PROCESS_WAIT_SEC,
          }
        : {
            sku: instance.name || 'Equipment',
            source: 'dxf-equipment',
            objectType: instance.objectType,
            category: instance.category,
            equipmentType: instance.equipmentType,
            ports,
            description: instance.description ?? '',
            processWaitSec: instance.processWaitSec ?? DEFAULT_STOCKER_PROCESS_WAIT_SEC,
          }),
      [EQUIPMENT_OVERLAY_PROP]: overlaySnapshot,
      dxfEquipmentId: instance.id,
      ...(blockName ? { blockName } : {}),
      ...(layer ? { dxfLayer: layer } : {}),
      ...(process ? { process } : {}),
      ...(workCell ? { workCell } : {}),
    },
  };
}

/**
 * Convert a DXF INSERT equipment stocker into a layout `stocker` element.
 * @deprecated Prefer {@link layoutElementFromEquipmentInstance}.
 */
export function layoutElementFromDxfStocker(instance: EquipmentInstance): LayoutElement | null {
  if (!isDxfInsertStocker(instance)) return null;
  return layoutElementFromEquipmentInstance(instance);
}

/** Download arbitrary JSON (same pattern as layout export). */
export function downloadJsonFile(data: unknown, filename: string): void {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
